import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { OnboardingUserType } from '@/services/onboarding.service';
import { cn } from '@/utils/cn';

/**
 * WelcomeStep Component
 *
 * First step of onboarding wizard - user type selection
 * Only shown for freemium signups (not invited users)
 */

interface UserTypeOptionProps {
  label: string;
  description: string;
  materialIcon: string;
  selected: boolean;
  onClick: () => void;
}

function UserTypeOption({ label, description, materialIcon, selected, onClick }: UserTypeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={0}
      className={cn(
        'w-full p-6 rounded-xl border-2 transition-all duration-200',
        'text-left hover:border-primary-400 hover:shadow-md',
        // Only show focus ring on keyboard navigation (Tab), not on click or auto-focus
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        selected
          ? 'border-primary-500 bg-primary-50 shadow-md focus-visible:ring-0'
          : 'border-gray-200 bg-white'
      )}
      aria-pressed={selected}
    >
      <div className="flex items-start gap-4">
        {/* Icon - using Material Symbols for visual consistency */}
        <div
          className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-colors',
            selected ? 'bg-primary-500 text-primary-contrast' : 'bg-gray-100 text-gray-600'
          )}
        >
          <span className="material-symbols-outlined text-[28px]" aria-hidden="true">
            {materialIcon}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              'text-lg font-semibold mb-1 transition-colors',
              selected ? 'text-primary-900' : 'text-gray-900'
            )}
          >
            {label}
          </h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>

        {/* Checkmark */}
        <div className="flex-shrink-0">
          {selected && (
            <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center animate-fade-in">
              <Icon name={ICONS.CHECK} size="sm" color="text-white" ariaHidden />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

interface WelcomeStepProps {
  selectedType: OnboardingUserType | null;
  onSelectType: (type: OnboardingUserType) => void;
}

export function WelcomeStep({ selectedType, onSelectType }: WelcomeStepProps) {
  const { t } = useTranslation();

  const userTypes: Array<{
    type: OnboardingUserType;
    label: string;
    description: string;
    materialIcon: string;
  }> = [
    {
      type: 'individual',
      label: t('onboarding_welcome_role_individual_label', { defaultValue: 'Individual creator / Small business' }),
      description: t('onboarding_welcome_role_individual_desc', { defaultValue: 'Just me managing my social accounts' }),
      materialIcon: 'person',
    },
    {
      type: 'team',
      label: t('onboarding_welcome_role_team_label', { defaultValue: 'Marketing team' }),
      description: t('onboarding_welcome_role_team_desc', { defaultValue: 'Multiple people collaborating on content' }),
      materialIcon: 'group',
    },
    {
      type: 'agency',
      label: t('onboarding_welcome_role_agency_label', { defaultValue: 'Brand Portfolio managing clients' }),
      description: t('onboarding_welcome_role_agency_desc', { defaultValue: 'Managing multiple brands' }),
      materialIcon: 'apartment',
    },
  ];

  return (
    <div className="text-center">
      <h2 className="text-2xl font-semibold text-gray-900 mb-2">
        {t('onboarding_welcome_title', { defaultValue: 'Welcome to Ägile Ässets!' })}
      </h2>
      <p className="text-gray-600 mb-8">
        {t('onboarding_welcome_description')}
      </p>

      <div className="space-y-3">
        {userTypes.map(({ type, label, description, materialIcon }) => (
          <UserTypeOption
            key={type}
            label={label}
            description={description}
            materialIcon={materialIcon}
            selected={selectedType === type}
            onClick={() => onSelectType(type)}
          />
        ))}
      </div>
    </div>
  );
}
